/**
 * Centralized Date Utilities for Yumii
 */

/**
 * Extracts a Date from a Yumii Order ID (format: ord_XXXX_DDMMYY)
 * This is used as a fallback when the timestamp field is missing or corrupted.
 */
export const extractDateFromOrderId = (orderId: string | undefined | null): Date | null => {
    if (!orderId || typeof orderId !== 'string') return null;
    
    try {
        const parts = orderId.split('_');
        const dateStr = parts[parts.length - 1]; // "DDMMYY"
        
        if (!dateStr || dateStr.length !== 6 || isNaN(Number(dateStr))) return null;
        
        const day = parseInt(dateStr.substring(0, 2));
        const month = parseInt(dateStr.substring(2, 4)) - 1; // 0-indexed month
        const year = 2000 + parseInt(dateStr.substring(4, 6));
        
        const date = new Date(year, month, day);
        // We return the date at the start of that day (local time)
        return isNaN(date.getTime()) ? null : date;
    } catch (e) {
        return null;
    }
};

/**
 * Parses various date formats (Firestore Timestamp, ISO string, Date object) into a JS Date
 */
export const parseDate = (field: any, fallback: Date = new Date(), orderId?: string): Date => {
    // Determine the ultimate fallback (prefer orderId parsing over 'now')
    const smartFallback = orderId ? (extractDateFromOrderId(orderId) || fallback) : fallback;

    if (field === null || field === undefined) return smartFallback;
    
    // Firestore Timestamp
    if (typeof field.toDate === 'function') return field.toDate();
    
    // Firestore-like seconds/nanoseconds object
    if (field && typeof field === 'object' && field.seconds !== undefined) {
        return new Date(field.seconds * 1000);
    }

    // Corrupted serverTimestamp sentinel (plain object instead of FieldValue)
    if (field && typeof field === 'object' && field._methodName === 'serverTimestamp') {
        return smartFallback;
    }
    
    // Date object
    if (field instanceof Date) return isNaN(field.getTime()) ? smartFallback : field;
    
    // ISO string or timestamp number
    const d = new Date(field);
    return (isNaN(d.getTime()) || d.getTime() === 0) ? smartFallback : d;
};

/**
 * Formats a date into a readable French string (e.g., "Aujourd'hui, 14:30" or "12/04/2026, 14:30")
 */
export const formatDateReadable = (date: Date | any): string => {
    const d = parseDate(date);
    if (!d || isNaN(d.getTime())) return 'Date inconnue';

    const now = new Date();
    const isToday = d.getDate() === now.getDate() && 
                    d.getMonth() === now.getMonth() && 
                    d.getFullYear() === now.getFullYear();
    
    // Create a NEW date for yesterday to avoid mutating 'now'
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = yesterday.toDateString() === d.toDateString();
    
    const timeStr = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    
    if (isToday) return `Aujourd'hui, ${timeStr}`;
    if (isYesterday) return `Hier, ${timeStr}`;
    
    return `${d.toLocaleDateString('fr-FR')} ${timeStr}`;
};

/**
 * Formats a date into a readable French date without time (e.g., "Aujourd'hui" or "12/04/2026")
 */
export const formatDateOnly = (date: Date | any): string => {
    const d = parseDate(date);
    if (!d || isNaN(d.getTime())) return 'Date inconnue';

    const now = new Date();
    const isToday = d.getDate() === now.getDate() && 
                    d.getMonth() === now.getMonth() && 
                    d.getFullYear() === now.getFullYear();
    
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = yesterday.toDateString() === d.toDateString();
    
    if (isToday) return `Aujourd'hui`;
    if (isYesterday) return `Hier`;
    
    return d.toLocaleDateString('fr-FR');
};

/**
 * Returns the start of the current week (Monday, 00:00:00)
 */
export const getStartOfWeek = (): Date => {
    const now = new Date();
    const day = now.getDay();
    const diff = (day === 0 ? 6 : day - 1); // Adjust for Monday start
    const start = new Date(now);
    start.setDate(now.getDate() - diff);
    start.setHours(0, 0, 0, 0);
    return start;
};

/**
 * Checks if a restaurant should be online according to its schedule (Algeria Time)
 */
export const checkRestaurantShouldBeOnline = (openingHours: any, useSchedule: boolean): boolean => {
    if (!useSchedule || !openingHours) return true; // Default to manual control if schedule not used

    try {
        // Get Algeria Time
        const now = new Date();
        const formatter = new Intl.DateTimeFormat('fr-FR', {
            timeZone: 'Africa/Algiers',
            weekday: 'long',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
        
        const parts = formatter.formatToParts(now);
        const dayMap: Record<string, string> = {
            'lundi': 'monday',
            'mardi': 'tuesday',
            'mercredi': 'wednesday',
            'jeudi': 'thursday',
            'vendredi': 'friday',
            'samedi': 'saturday',
            'dimanche': 'sunday'
        };
        
        const currentDayFr = parts.find(p => p.type === 'weekday')?.value.toLowerCase();
        const currentHour = parts.find(p => p.type === 'hour')?.value;
        const currentMinute = parts.find(p => p.type === 'minute')?.value;

        if (!currentDayFr || !currentHour || !currentMinute) return true;

        const currentDay = dayMap[currentDayFr];
        const currentTimeMinutes = parseInt(currentHour) * 60 + parseInt(currentMinute);
        const daySched = openingHours[currentDay];

        if (!daySched || daySched.isClosed) return false;

        const [openH, openM] = daySched.open.split(':').map(Number);
        const [closeH, closeM] = daySched.close.split(':').map(Number);
        
        const openTimeMinutes = openH * 60 + openM;
        const closeTimeMinutes = closeH * 60 + closeM;

        if (closeTimeMinutes < openTimeMinutes) {
            // Night shift (ex: 22:00 - 02:00)
            return currentTimeMinutes >= openTimeMinutes || currentTimeMinutes < closeTimeMinutes;
        } else {
            // Normal shift
            return currentTimeMinutes >= openTimeMinutes && currentTimeMinutes < closeTimeMinutes;
        }
    } catch (e) {
        console.error("Error checking restaurant schedule:", e);
        return true; // Fallback to avoid accidentally closing restaurants on code errors
    }
};

/**
 * Checks if a driver should be online according to their schedule (Algeria Time)
 */
export const checkLivreurShouldBeOnline = (schedule: any): boolean => {
    if (!schedule) return false;

    try {
        const now = new Date();
        const formatter = new Intl.DateTimeFormat('fr-FR', {
            timeZone: 'Africa/Algiers',
            weekday: 'long',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
        
        const parts = formatter.formatToParts(now);
        const dayMap: Record<string, string> = {
            'lundi': 'monday',
            'mardi': 'tuesday',
            'mercredi': 'wednesday',
            'jeudi': 'thursday',
            'vendredi': 'friday',
            'samedi': 'saturday',
            'dimanche': 'sunday'
        };
        
        const currentDayFr = parts.find(p => p.type === 'weekday')?.value.toLowerCase();
        const currentHour = parts.find(p => p.type === 'hour')?.value;
        const currentMinute = parts.find(p => p.type === 'minute')?.value;

        if (!currentDayFr || !currentHour || !currentMinute) return false;

        const currentDay = dayMap[currentDayFr];
        const currentTimeMinutes = parseInt(currentHour) * 60 + parseInt(currentMinute);
        const daySched = schedule[currentDay];

        if (!daySched || !daySched.active || !daySched.start || !daySched.end) return false;

        const [openH, openM] = daySched.start.split(':').map(Number);
        const [closeH, closeM] = daySched.end.split(':').map(Number);
        
        const openTimeMinutes = openH * 60 + openM;
        const closeTimeMinutes = closeH * 60 + closeM;

        if (closeTimeMinutes < openTimeMinutes) {
            // Night shift (ex: 22:00 - 02:00)
            return currentTimeMinutes >= openTimeMinutes || currentTimeMinutes < closeTimeMinutes;
        } else {
            // Normal shift
            return currentTimeMinutes >= openTimeMinutes && currentTimeMinutes < closeTimeMinutes;
        }
    } catch (e) {
        console.error("Error checking livreur schedule:", e);
        return false;
    }
};
/**
 * Calculates remaining time until a given expiry date and returns a formatted string.
 * Returns null if expired or no expiry provided.
 */
export const getTimeRemaining = (expiry: any): string | null => {
    if (!expiry) return null;
    const expiryDate = parseDate(expiry);
    const now = new Date();
    const diff = expiryDate.getTime() - now.getTime();
    if (diff <= 0) return null;

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days >= 1) return `${days}j restant${days > 1 ? 's' : ''}`;

    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours >= 1) return `${hours}h restante${hours > 1 ? 's' : ''}`;

    const minutes = Math.floor(diff / (1000 * 60));
    return `${minutes}m restante${minutes > 1 ? 's' : ''}`;
};
